// Track Z preset helpers (Phase 1)
//
// Goals:
// - Provide deterministic per-track base Z positions for presets
// - Keep track 1 anchored at z=0
// - Provide pure clamp helpers so tracks cannot cross neighbors in Z
//
// Notes:
// - "layer" refers to track.layer (1..N for visual layer tracks)
// - Z convention: larger Z is closer to camera (camera is typically at +Z looking toward -Z)
// - We place deeper tracks at more negative Z.

export const TRACK_Z_PRESETS = [
  'Singularity',
  'Tight',
  'Mid',
  'Large',
  'Extra Large',
  'Colossal',
  'Cosmic'
]

export const TRACK_Z_PRESET_CFG = {
  // Matches current behavior conceptually, but shifted so Track 1 is exactly z=0.
  // Old baseline was ~z = -(layer/10). New baseline is z = -((layer-1)/10).
  Singularity: { mode: 'singularity' },

  // Geometric spacing presets (ratio=2.0 by default)
  Tight: { mode: 'geometric', base: 5, ratio: 2 },
  Mid: { mode: 'geometric', base: 10, ratio: 2 },
  Large: { mode: 'geometric', base: 20, ratio: 2 },
  'Extra Large': { mode: 'geometric', base: 60, ratio: 2 },
  Colossal: { mode: 'geometric', base: 180, ratio: 2 },
  Cosmic: { mode: 'geometric', base: 540, ratio: 2 },
}

export const DEFAULT_TRACK_Z_PRESET = 'Singularity'

export function isTrackZPreset(preset) {
  return TRACK_Z_PRESETS.includes(preset)
}

function toLayerIndex(layer) {
  const l = Number(layer)
  if (!Number.isFinite(l)) return 0
  return Math.max(0, Math.floor(l))
}

/**
 * Baseline "Singularity" Z for a track layer.
 * Track 1 is exactly 0; Track 2 is -0.1; Track 3 is -0.2, ...
 */
export function singularityTrackBaseZ(layer) {
  const l = toLayerIndex(layer)
  if (l <= 1) return 0
  return -((l - 1) / 10)
}

/**
 * Geometric track baseZ:
 * - Track 1 is 0
 * - gap(1->2) = base
 * - gap(i->i+1) = base * ratio^(i-1)
 *
 * Example (base=5, ratio=2):
 * - Track 1: 0
 * - Track 2: -5
 * - Track 3: -15
 * - Track 4: -35
 */
export function geometricTrackBaseZ(layer, base, ratio) {
  const l = toLayerIndex(layer)
  if (l <= 1) return 0
  const b = Number(base)
  const r = Number(ratio)
  if (!Number.isFinite(b) || b <= 0) return 0
  if (!Number.isFinite(r) || r <= 0) return 0

  let sum = 0
  // sum gaps from 1->2 up to (l-1)->l
  for (let i = 1; i <= l - 1; i++) {
    sum += b * Math.pow(r, i - 1)
  }
  return -sum
}

export function trackPresetBaseZ(preset, layer) {
  const key = isTrackZPreset(preset) ? preset : DEFAULT_TRACK_Z_PRESET
  const cfg = TRACK_Z_PRESET_CFG[key] || TRACK_Z_PRESET_CFG[DEFAULT_TRACK_Z_PRESET]

  if (!cfg || cfg.mode === 'singularity') return singularityTrackBaseZ(layer)
  if (cfg.mode === 'geometric') return geometricTrackBaseZ(layer, cfg.base, cfg.ratio)
  return singularityTrackBaseZ(layer)
}

/**
 * Delta from Singularity baseline for a given preset.
 * Useful when you want to keep existing strip-level z logic and apply a preset offset.
 */
export function trackPresetDeltaZ(preset, layer) {
  const z0 = singularityTrackBaseZ(layer)
  const z1 = trackPresetBaseZ(preset, layer)
  return z1 - z0
}

/**
 * Clamp an effective track Z so it cannot exceed neighbors.
 * - prevZ: track above/closer (e.g., Track 1 for Track 2) => higher Z
 * - nextZ: track below/farther (e.g., Track 3 for Track 2) => lower Z
 */
export function clampTrackZ({ desiredZ, prevZ, nextZ, eps = 1e-3 }) {
  let z = Number(desiredZ)
  if (!Number.isFinite(z)) z = 0
  const e = Number.isFinite(eps) ? Math.max(0, eps) : 0

  if (Number.isFinite(prevZ)) z = Math.min(z, prevZ - e)
  if (Number.isFinite(nextZ)) z = Math.max(z, nextZ + e)

  return z
}

/**
 * Clamp a track's Z offset (relative to its baseZ) so it cannot cross neighbors.
 */
export function clampTrackZOffset({
  baseZ,
  desiredOffset,
  prevZ,
  nextZ,
  eps = 1e-3
}) {
  const bz = Number(baseZ)
  const off = Number(desiredOffset)
  const safeBase = Number.isFinite(bz) ? bz : 0
  const safeOff = Number.isFinite(off) ? off : 0

  const desiredZ = safeBase + safeOff
  const clampedZ = clampTrackZ({ desiredZ, prevZ, nextZ, eps })
  return clampedZ - safeBase
}

/**
 * Sort layer tracks in ascending layer order (1,2,3,...).
 * Accepts either an array of tracks or an object map (uuid->track).
 */
export function sortLayerTracks(tracks) {
  const arr = Array.isArray(tracks)
    ? tracks.slice()
    : tracks && typeof tracks === 'object'
      ? Object.values(tracks)
      : []

  return arr
    .filter((t) => t && t.type === 'layer')
    .sort((a, b) => (Number(a.layer) || 0) - (Number(b.layer) || 0))
}


